import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Sparkles, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getActiveFestival, FESTIVALS_2026 } from '../../data/festivals';

/**
 * Auto-detecting Festival Banner
 * - Activates 7 days BEFORE festival start
 * - Deactivates 1 day AFTER festival end
 * - Dismissible per-session (sessionStorage)
 * - Animated entrance + floating particles
 * - DEBUG: append ?previewFestival=<festival-id> to URL to force-preview any banner
 */
const FestivalBanner = () => {
  const [festival, setFestival] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Debug preview mode via URL param (always shows requested festival)
    const params = new URLSearchParams(window.location.search);
    const previewId = params.get('previewFestival');
    let f = null;
    if (previewId) {
      f = FESTIVALS_2026.find(x => x.id === previewId) || null;
    } else {
      f = getActiveFestival();
    }
    if (!f) return;
    const isDismissed = sessionStorage.getItem(`festival-dismissed-${f.id}`) === 'true';
    setDismissed(isDismissed);
    setFestival(f);
  }, []);

  const handleDismiss = () => {
    setDismissed(true);
    if (festival) sessionStorage.setItem(`festival-dismissed-${festival.id}`, 'true');
  };

  if (!festival || dismissed) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -80, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 260, damping: 22 }}
        data-testid={`festival-banner-${festival.id}`}
        className={`relative bg-gradient-to-r ${festival.color_from} ${festival.color_via} ${festival.color_to} ${festival.text_color} overflow-hidden`}
        role="banner"
        aria-label={`${festival.name} festival promotion`}
      >
        {/* Floating particles */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {[...Array(6)].map((_, i) => (
            <motion.div
              key={i}
              initial={{ x: -30, y: Math.random() * 40 - 20, opacity: 0.3 }}
              animate={{
                x: typeof window !== 'undefined' ? window.innerWidth + 30 : 1200,
                y: Math.random() * 40 - 20,
                opacity: [0.3, 0.7, 0.3],
              }}
              transition={{
                duration: 8 + i * 1.5,
                repeat: Infinity,
                ease: 'linear',
                delay: i * 1.2,
              }}
              className="absolute top-1/2 text-2xl"
              style={{ filter: 'drop-shadow(0 0 6px rgba(255,255,255,0.6))' }}
            >
              {festival.emoji}
            </motion.div>
          ))}
        </div>

        <div className="relative max-w-7xl mx-auto px-4 py-3 sm:py-4">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6">
            <motion.div
              animate={{ rotate: [0, 12, -12, 0], scale: [1, 1.15, 1] }}
              transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
              className="text-3xl sm:text-4xl flex-shrink-0"
              aria-hidden="true"
            >
              {festival.emoji}
            </motion.div>

            <div className="flex-1 text-center sm:text-left">
              <p className="font-extrabold text-sm sm:text-base md:text-lg leading-tight">
                {festival.headline}
              </p>
              <p className="text-xs sm:text-sm opacity-90 mt-0.5 leading-snug">
                {festival.sub}
              </p>
            </div>

            <Link
              to={festival.cta_link}
              data-testid={`festival-banner-cta-${festival.id}`}
              className="group inline-flex items-center gap-2 bg-white text-slate-900 px-5 py-2 rounded-full text-sm font-bold shadow-md hover:shadow-xl hover:scale-105 transition-all flex-shrink-0"
            >
              <Sparkles className="w-4 h-4 text-amber-500 group-hover:rotate-12 transition-transform" />
              {festival.cta_text}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>

        <button
          onClick={handleDismiss}
          aria-label="Dismiss festival banner"
          data-testid={`festival-banner-dismiss-${festival.id}`}
          className="absolute top-2 right-2 sm:top-1/2 sm:-translate-y-1/2 sm:right-4 p-1.5 rounded-full hover:bg-white/20 transition-colors"
        >
          <X className="w-4 h-4 opacity-80" />
        </button>
      </motion.div>
    </AnimatePresence>
  );
};

export default FestivalBanner;
